import { ComponentProps, CSSProperties } from 'react';
import { ISiteSectionEntry } from '../../../../contentful/types/ISiteSection';
import { Label, StyledButton, StyledLink } from './SiteSectionItem.styles';
import { useElementWidth } from '../../../../hooks/useElementWidth';

import { getUrlForPageId } from '../../../../utils/getUrlForLink';
import { useCommonData } from '../../../../common-data/useCommonData';
import { RouterLink } from '../../link/RouterLink';
import { isStringTranslatable } from '../../../../utils/isStringTranslatable';
import FormattedTextForTranslation from '../../text/FormattedTextForTranslation';

interface INavItemProps {
	siteSection: ISiteSectionEntry;
	onSiteSectionIdChange: (id: string) => void;
	isCurrent: boolean;
	collapsed: boolean;
}

export function SiteSectionItem({
	siteSection,
	isCurrent,
	collapsed,
	onSiteSectionIdChange,
}: INavItemProps) {
	const { pathToPageIdMap } = useCommonData();
	const state = collapsed ? 'collapsed' : 'expanded';
	const ariaLabel = siteSection.fields.title;

	const { title } = siteSection.fields;
	const titleParts = title?.split(' ');

	const renderContent = () => (
		<Label>
			{titleParts?.map((part, index) => {
				const isTranslatable = isStringTranslatable(part);

				return (
					<FormattedTextForTranslation
						key={index} /* eslint-disable-line react/no-array-index-key */
						index={index}
						isTranslatable={isTranslatable}
						part={part}
						stringParts={titleParts}
					/>
				);
			})}
		</Label>
	);

	if (siteSection.fields.landingPage) {
		return (
			<NavLink
				href={getUrlForPageId(
					siteSection.fields.landingPage.fields.id,
					pathToPageIdMap,
				)}
				data-state={state}
				aria-current={isCurrent ? 'page' : undefined}
				aria-label={ariaLabel}
			>
				{renderContent()}
			</NavLink>
		);
	}

	return (
		<NavButton
			data-state={state}
			aria-current={isCurrent ? true : undefined}
			onClick={() => onSiteSectionIdChange(siteSection.fields.id)}
			aria-label={ariaLabel}
		>
			{renderContent()}
		</NavButton>
	);
}

function NavLink({ children, ...props }: ComponentProps<typeof RouterLink>) {
	const { width, ref } = useElementWidth<HTMLAnchorElement>();

	return (
		<StyledLink
			{...props}
			ref={ref}
			style={
				{ '--link-width': width ? `${width}px` : undefined } as CSSProperties
			}
		>
			{children}
		</StyledLink>
	);
}

function NavButton({ children, ...props }: ComponentProps<'button'>) {
	const { width, ref } = useElementWidth<HTMLButtonElement>();

	return (
		<StyledButton
			{...props}
			ref={ref}
			style={
				{ '--link-width': width ? `${width}px` : undefined } as CSSProperties
			}
		>
			{children}
		</StyledButton>
	);
}
